import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../config.js';

import '../../styles/global.css';
import '../../styles/requests.css';

import PageHeader from '../general/pageHeader.js';
import Loading from '../general/loading.js';
import CreateAmenity from './CreateAmenity.js'; // Import the new component

import { Autocomplete, TextField, Checkbox, FormControlLabel, Button } from "@mui/material";
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const DAYS_MAP = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const ROLE_OPTIONS = ["Resident", "Owner", "Manager", "Tenant", "Admin", "Staff"];

// Create amenity option constant
const CREATE_AMENITY_OPTION = {
  _id: 'create-new-amenity',
  name: 'Create an Amenity',
  isCreateOption: true
};

// Reusable TextField styling
const TEXT_FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': { borderColor: '#445485' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#445485' },
};

// Button styling variants
const BUTTON_STYLES = {
  primary: {
    borderColor: '#445485',
    color: '#445485',
    '&:hover': {
      borderColor: '#3a4674',
      backgroundColor: 'rgba(68, 84, 133, 0.04)',
    },
    fontSize: '0.75rem',
    minWidth: 'auto',
    px: 2
  },
  danger: {
    borderColor: '#dc2626',
    color: '#dc2626',
    '&:hover': {
      borderColor: '#b91c1c',
      backgroundColor: 'rgba(220, 38, 38, 0.04)',
    },
    fontSize: '0.75rem',
    minWidth: 'auto',
    px: 2
  }
};

// Custom hook for form state management
const useAmenitySettings = (amenitiesDataArray) => {
  const [state, setState] = useState({
    allowedRoles: [],
    bookingWindowDays: 30,
    selectedDays: [true, true, true, true, true, true, true],
    timeSlots: [{ startTime: "08:00", endTime: "20:00" }],
    slotSizeMinutes: 30,
    maxDurationMinutes: 120,
    maxBookingsPerUserPerDay: 1,
    isApprovalRequired: false,
  });

  // Hydrate form when data changes
  useEffect(() => {
    // Extract the first item from the array
    const amenitiesData = amenitiesDataArray?.[0];
    
    if (!amenitiesData) {
      // Reset to default state when no data
      setState({
        allowedRoles: [],
        bookingWindowDays: 30,
        selectedDays: [true, true, true, true, true, true, true],
        timeSlots: [{ startTime: "08:00", endTime: "20:00" }],
        slotSizeMinutes: 30,
        maxDurationMinutes: 120,
        maxBookingsPerUserPerDay: 1,
        isApprovalRequired: false,
      });
      return;
    }

    // Handle case mismatch for roles - capitalize first letter to match options
    const normalizeRoles = (roles) => {
      if (!roles || !Array.isArray(roles)) return [];
      return roles
        .map(role =>
          typeof role === "string"
            ? role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
            : role
        )
        .filter(role => ROLE_OPTIONS.includes(role)); 
    };

    setState({
      allowedRoles: normalizeRoles(amenitiesData.allowedRoles),
      bookingWindowDays: amenitiesData.bookingWindowDays ?? 30,
      selectedDays: DAYS_MAP.map(day => amenitiesData.allowedDaysOfWeek?.includes(day) ?? true),
      timeSlots: amenitiesData.allowedTimeSlots?.length ? amenitiesData.allowedTimeSlots : [{ startTime: "08:00", endTime: "20:00" }],
      slotSizeMinutes: amenitiesData.slotSizeMinutes ?? 30,
      maxDurationMinutes: amenitiesData.maxDurationMinutes ?? 120,
      maxBookingsPerUserPerDay: amenitiesData.maxBookingsPerUserPerDay ?? 1,
      isApprovalRequired: amenitiesData.isApprovalRequired ?? false
    });
  }, [amenitiesDataArray]);

  const updateState = (key, value) => {
    setState(prev => ({ ...prev, [key]: value }));
  };

  return [state, updateState];
};

const AmenitySettings = ({ onBack }) => {
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedAmenity, setSelectedAmenity] = useState(null);

  const { data: properties, isLoading } = useQuery({
    queryKey: ["properties"],
    queryFn: fetchProperties,
  });

  const { data: amenities = [], refetch: refetchAmenities } = useQuery({
    queryKey: ["amenities", selectedProperty?.value],
    queryFn: () => fetchAmenities(selectedProperty?.value),
    enabled: !!selectedProperty,
  });

  const { data: amenitiesData } = useQuery({
    queryKey: ["amenitySettings", selectedAmenity?._id],
    queryFn: () => fetchAmenitySettings(selectedAmenity?._id),
    enabled: !!selectedAmenity && !selectedAmenity?.isCreateOption,
  });

  console.log('Selected Amenity:', selectedAmenity);
  console.log('selected amenity id:', selectedAmenity?._id);
  console.log('Amenity settings data:', amenitiesData);

  const [formState, updateFormState] = useAmenitySettings(amenitiesData);

  // Auto-select first property
  useEffect(() => {
    if (properties?.length > 0 && !selectedProperty) {
      setSelectedProperty(properties[0]);
    }
  }, [properties, selectedProperty]);

  // Reset selected amenity when property changes
  useEffect(() => {
    setSelectedAmenity(null);
  }, [selectedProperty]);

  // Create enhanced amenities list with create option
  const enhancedAmenities = amenities?.length > 0 
    ? [CREATE_AMENITY_OPTION, ...amenities]
    : [CREATE_AMENITY_OPTION];

  // Handle amenity creation success
  const handleAmenityCreated = (newAmenity) => {
    refetchAmenities(); // Refresh the amenities list
    setSelectedAmenity(newAmenity); // Select the newly created amenity
  };

  // Day selection handlers
  const dayHandlers = {
    handleDayChange: (index, checked) => {
      const newSelectedDays = [...formState.selectedDays];
      newSelectedDays[index] = checked;
      updateFormState('selectedDays', newSelectedDays);
    },
    handleSelectAll: () => updateFormState('selectedDays', [true, true, true, true, true, true, true]),
    handleSelectWeekdays: () => updateFormState('selectedDays', [false, true, true, true, true, true, false]),
    handleSelectWeekends: () => updateFormState('selectedDays', [true, false, false, false, false, false, true]),
    handleClearAll: () => updateFormState('selectedDays', [false, false, false, false, false, false, false]),
  };

// Time slot handlers
const timeSlotHandlers = {
  handleAddTimeSlot: () => {
    updateFormState('timeSlots', [...formState.timeSlots, { startTime: "", endTime: "" }]);
  },
  
  handleRemoveTimeSlot: (index) => {
    if (formState.timeSlots.length > 1) {
      updateFormState('timeSlots', formState.timeSlots.filter((_, i) => i !== index));
    }
  },
  
  handleTimeSlotChange: (index, field, value) => {
    const updatedSlots = formState.timeSlots.map((slot, i) =>
      i === index ? { ...slot, [field]: value } : slot
    );
    
    // Validate after updating
    if (field === 'endTime' || field === 'startTime') {
      const currentSlot = updatedSlots[index];
      
      // Only validate if both times are set
      if (currentSlot.startTime && currentSlot.endTime) {
        const start = dayjs(`2000-01-01 ${currentSlot.startTime}`);
        const end = dayjs(`2000-01-01 ${currentSlot.endTime}`);
        
        // Validation 1: Check if end time is before or equal to start time (extends to next day)
        if (end.isBefore(start) || end.isSame(start)) {
          toast.error('Time ranges cannot extend into the next day.');
          return; // Don't update if invalid
        }
        
        // Validation 2: Check for overlaps with other time slots
        const hasOverlap = updatedSlots.some((slot, i) => {
          if (i === index || !slot.startTime || !slot.endTime) return false;
          
          const otherStart = dayjs(`2000-01-01 ${slot.startTime}`);
          const otherEnd = dayjs(`2000-01-01 ${slot.endTime}`);
          
          // Check if ranges overlap
          // Overlap occurs if: start < otherEnd AND end > otherStart
          return start.isBefore(otherEnd) && end.isAfter(otherStart);
        });
        
        if (hasOverlap) {
          toast.error('Time ranges cannot overlap.');
          return; // Don't update if invalid
        }
      }
    }
    
    updateFormState('timeSlots', updatedSlots);
  },
};

  // Save handler
  const handleSaveSettings = async () => {
    if (!selectedAmenity?._id || !amenitiesData?.[0]) return;

    const allowedDaysOfWeek = DAYS_MAP.filter((_, idx) => formState.selectedDays[idx]);

    // Convert roles back to lowercase for backend
    const normalizedRoles = formState.allowedRoles.map(role => 
      typeof role === 'string' ? role.toLowerCase() : role
    );

    const payload = {
      amenity: selectedAmenity._id,
      allowedRoles: normalizedRoles,
      bookingWindowDays: formState.bookingWindowDays,
      allowedDaysOfWeek,
      allowedTimeSlots: formState.timeSlots,
      slotSizeMinutes: formState.slotSizeMinutes,
      maxDurationMinutes: formState.maxDurationMinutes,
      maxBookingsPerUserPerDay: formState.maxBookingsPerUserPerDay,
      isApprovalRequired: formState.isApprovalRequired
    };

    try {
      console.log('amenitiesData id:', amenitiesData[0]._id);
      await axios.put(
        `${config.API_URL}/v2/amenity/booking/setting/${amenitiesData[0]._id}`,
        payload,
        {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}}
      );
      toast.success("Settings saved successfully!");
    } catch (err) {
      console.error("Error saving settings:", err);
      toast.error("Failed to save settings.");
    }
  };

  // Destructure form state for cleaner JSX
  const {
    allowedRoles, bookingWindowDays, selectedDays, timeSlots,
    slotSizeMinutes, maxDurationMinutes, maxBookingsPerUserPerDay,
    isApprovalRequired
  } = formState;

  return (
    <div className="w-full">
      <PageHeader pageName="Amenity Settings" />

      <div className="mb-4">
        <button
          onClick={onBack}
          className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium transition"
        >
          ← Back
        </button>
      </div>

      {isLoading ? <Loading /> : (
        <>
          <div className="page-header">
            <Autocomplete
              options={properties}
              value={selectedProperty}
              onChange={(e, newVal) => setSelectedProperty(newVal)}
              renderInput={(params) => (
                <TextField {...params} label="Select Property" variant="outlined" fullWidth />
              )}
              sx={{ width: 350 }}
            />

            <Autocomplete
              options={enhancedAmenities}
              value={selectedAmenity || null}
              onChange={(e, newVal) => setSelectedAmenity(newVal)}
              isOptionEqualToValue={(option, value) => option?._id === value?._id}
              getOptionLabel={(option) => option?.name || ''}
              renderOption={(props, option) => (
                <li {...props} style={{ 
                  fontStyle: option.isCreateOption ? 'italic' : 'normal',
                  color: option.isCreateOption ? '#445485' : 'inherit',
                  fontWeight: option.isCreateOption ? 500 : 'normal'
                }}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Select Amenity" variant="outlined" fullWidth />
              )}
              disabled={!selectedProperty}
              sx={{ width: '350px' }}
            />
          </div>

          {/* Show Create Amenity Component */}
          {selectedAmenity?.isCreateOption && (
            <CreateAmenity 
              selectedProperty={selectedProperty}
              onAmenityCreated={handleAmenityCreated}
            />
          )}

          {/* Settings Form - Only show for existing amenities */}
          {selectedAmenity && !selectedAmenity.isCreateOption && (
            <div className="p-6 bg-white rounded-lg shadow-sm">
              {/* header */}
              <div className="text-center pb-6 border-b border-gray-200 mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">{selectedAmenity?.name} Settings</h2>
                <p className="text-gray-600">Configure settings for the selected amenity</p>
              </div>
          

            <div className="space-y-8">
                {/* Access Control Section */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#445485' }}></span>
                        Access & Approval
                    </h3>
                
                    <div className="space-y-4">
                        <div>
                        <label className="block font-medium text-gray-700 mb-2">Allowed Roles</label>
                        <Autocomplete
                            multiple
                            options={["Resident", "Owner", "Manager", "Tenant", "Admin", "Staff"]}
                            value={allowedRoles}
                            onChange={(e, newVal) => updateFormState('allowedRoles', newVal)}
                            renderInput={(params) => (
                            <TextField 
                                {...params} 
                                label="Select roles" 
                                variant="outlined"
                                sx={TEXT_FIELD_SX}
                            />
                            )}
                            sx={{
                            '& .MuiChip-root': {
                                backgroundColor: '#445485',
                                color: 'white',
                            },
                            }}
                        />
                        </div>
                        
                        <div>
                        <FormControlLabel 
                            control={
                            <Checkbox 
                                checked={isApprovalRequired}
                                onChange={(e) => updateFormState('isApprovalRequired', e.target.checked)}
                                sx={{
                                '&.Mui-checked': {
                                    color: '#445485',
                                },
                                }}
                            />
                            } 
                            label="Approval Required" 
                            className="text-gray-700"
                        />
                        </div>
                    </div>
                </div>

                {/* Booking Rules Section */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
                        <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                        Booking Rules
                    </h3>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                    <TextField
                        type="number"
                        label="Booking Window (days)"
                        variant="outlined"
                        fullWidth
                        value={bookingWindowDays}
                        onChange={(e) => updateFormState('bookingWindowDays', Number(e.target.value))}
                        sx={TEXT_FIELD_SX}
                    />
                    </div>
                    
                    <div>
                    <TextField
                        type="number"
                        label="Max Bookings per User per Day"
                        variant="outlined"
                        fullWidth
                        value={maxBookingsPerUserPerDay}
                        onChange={(e) => updateFormState('maxBookingsPerUserPerDay', Number(e.target.value))}
                        sx={TEXT_FIELD_SX}
                    />
                    </div>
                </div>

                    {/* Allowed Days */}
                    <div className="mb-4">
                        <label className="block font-medium text-gray-700 mb-3">Allowed Days of Week</label>
                        
                        {/* Quick Select Options */}
                        <div className="flex flex-wrap gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
                            <Button variant="outlined" size="small" onClick={dayHandlers.handleSelectAll} sx={BUTTON_STYLES.primary}>
                                All Days
                            </Button>
                            
                            <Button variant="outlined" size="small" onClick={dayHandlers.handleSelectWeekdays} sx={BUTTON_STYLES.primary}>
                                Weekdays
                            </Button>
                            
                            <Button variant="outlined" size="small" onClick={dayHandlers.handleSelectWeekends} sx={BUTTON_STYLES.primary}>
                                Weekends
                            </Button>
                            
                            <Button variant="outlined" size="small" onClick={dayHandlers.handleClearAll} sx={BUTTON_STYLES.danger}>
                                Clear All
                            </Button>
                        </div>
                        
                        {/* Individual Day Checkboxes */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                            {DAY_NAMES.map((day, index) => (
                                <FormControlLabel
                                    key={day}
                                    control={
                                        <Checkbox 
                                            checked={selectedDays[index]}
                                            onChange={(e) => dayHandlers.handleDayChange(index, e.target.checked)}
                                            sx={{
                                                '&.Mui-checked': {
                                                    color: '#445485',
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <span className="text-sm font-medium text-gray-700">
                                            {day.slice(0, 3)}
                                        </span>
                                    }
                                    className="m-0"
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Time & Duration Section */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Time & Duration Settings
                </h3>
                
                <label className="block font-medium text-gray-700 mb-3">Allowed Time Slots</label>
                
                {/* Time Slots List */}
                <div className="space-y-3">
                    {timeSlots.map((slot, index) => (
                    <LocalizationProvider dateAdapter={AdapterDayjs} key={index}>
                    <div className="flex flex-wrap gap-3 items-end">
                        {/* Start Time */}
                        <div className="flex-1 min-w-32">
                        <TimePicker
                            label="Start Time"
                            value={slot.startTime ? dayjs(slot.startTime, 'HH:mm') : null}
                            onChange={(newValue) =>
                            timeSlotHandlers.handleTimeSlotChange(index, 'startTime', newValue ? newValue.format('HH:mm') : '')
                            }
                            slotProps={{
                            textField: {
                                fullWidth: true,
                                sx: TEXT_FIELD_SX,
                            },
                            }}
                        />
                        </div>

                        {/* End Time */}
                        <div className="flex-1 min-w-32">
                        <TimePicker
                            label="End Time"
                            value={slot.endTime ? dayjs(slot.endTime, 'HH:mm') : null}
                            onChange={(newValue) =>
                            timeSlotHandlers.handleTimeSlotChange(index, 'endTime', newValue ? newValue.format('HH:mm') : '')
                            }
                            slotProps={{
                            textField: {
                                fullWidth: true,
                                sx: TEXT_FIELD_SX,
                            },
                            }}
                        />
                        </div>

                        {/* Remove Button */}
                        <Button 
                        variant="outlined" 
                        color="error"
                        onClick={() => timeSlotHandlers.handleRemoveTimeSlot(index)}
                        className="h-14"
                        sx={{ minWidth: 'auto', px: 2 }}
                        >
                        Remove
                        </Button>
                    </div>
                    </LocalizationProvider>
                    ))}
                </div>

                {/* Add Time Slot Button with spacing */}
                <Button 
                    variant="outlined"
                    onClick={timeSlotHandlers.handleAddTimeSlot}
                    sx={{
                    ...BUTTON_STYLES.primary,
                    mt: 3, 
                    mb: 4, 
                    }}
                >
                    + Add Time Slot
                </Button>

                {/* Duration Settings */}
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                    <TextField
                        type="number"
                        label="Slot Size (minutes)"
                        variant="outlined"
                        value={slotSizeMinutes}
                        onChange={(e) => updateFormState('slotSizeMinutes', Number(e.target.value))}
                        fullWidth
                        sx={TEXT_FIELD_SX}
                    />
                    </div>

                    <div>
                    <TextField
                        type="number"
                        label="Max Duration (minutes)"
                        variant="outlined"
                        fullWidth
                        value={maxDurationMinutes}
                        onChange={(e) => updateFormState('maxDurationMinutes', Number(e.target.value))}
                        sx={TEXT_FIELD_SX}
                    />
                    </div>
                </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-6 border-t border-gray-200">
                <Button 
                  variant="contained" 
                  size="large"
                  onClick={handleSaveSettings}
                  sx={{
                    backgroundColor: '#C86B2C',
                    '&:hover': { backgroundColor: '#b55d26' },
                  }}
                >
                  Save Settings
                </Button>
                </div>
            </div>
          </div>
        )}
      </>
    )}
  </div>
);
}

const fetchProperties = async () => {
    const response = await axios.get(`${config.API_URL}/v2/property`, {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}});
    return response.data.map(item => ({
        value: item._id,
        label: item.name,
        amenities: item.amenities
    }));
}

const fetchAmenities = async (propertyId) => {
    const response = await axios.get(
        `${config.API_URL}/v2/amenity?propertyId=${propertyId}`,
        {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}}
    );
    console.log('Fetched amenities for property:', response.data);

    return (response.data || []).filter(
    (amenity) => amenity.property._id === propertyId
  );
};

const fetchAmenitySettings = async (amenityId) => {
    const response = await axios.get(
        `${config.API_URL}/v2/amenity/booking/setting?amenity=${amenityId}`,
        {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}}
    );
    console.log('Fetched settings:', response.data);

    return response.data.results || [];
};

export default AmenitySettings;